import { WebPCodec, WorkerQueue } from '@playcanvas/splat-transform';
import { Color, Quat, Vec3, createGraphicsDevice } from 'playcanvas';

import { registerCameraPosesEvents } from './camera-poses';
import { CommandQueue } from './command-queue';
import { registerDocEvents } from './doc';
import { EditHistory } from './edit-history';
import { registerEditorEvents } from './editor';
import { Events } from './events';
import { initFileHandler } from './file-handler';
import { registerIframeApi } from './iframe-api';
import { registerPreferences } from './preferences';
import { registerPublishEvents } from './publish';
import { registerSaveRevisionEvents } from './save-revision';
import { registerRenderEvents } from './render';
import { Scene } from './scene';
import { getSceneConfig } from './scene-config';
import { registerSelectionEvents } from './selection';
import { registerSequenceEvents } from './sequence';
import { ShortcutManager } from './shortcut-manager';
import { registerTimelineEvents } from './timeline';
import { BoxSelection } from './tools/box-selection';
import { BrushSelection } from './tools/brush-selection';
import { EyedropperSelection } from './tools/eyedropper-selection';
import { FloodSelection } from './tools/flood-selection';
import { LassoSelection } from './tools/lasso-selection';
import { MeasureTool } from './tools/measure-tool';
import { MoveTool } from './tools/move-tool';
import { OrientTool } from './tools/orient-tool';
import { PolygonSelection } from './tools/polygon-selection';
import { RectSelection } from './tools/rect-selection';
import { RotateTool } from './tools/rotate-tool';
import { ScaleTool } from './tools/scale-tool';
import { SphereSelection } from './tools/sphere-selection';
import { ToolManager } from './tools/tool-manager';
import { registerTrackManagerEvents } from './track-manager';
import { registerTransformHandlerEvents } from './transform-handler';
import { BoundDimensionsOverlay } from './ui/bound-dimensions-overlay';
import { EditorUI } from './ui/editor';
import { i18n } from './ui/localization';
import { registerSelectCursor } from './ui/select-cursor';

declare global {
    interface LaunchParams {
        readonly files: FileSystemFileHandle[];
    }

    interface Window {
        launchQueue: {
            setConsumer: (callback: (launchParams: LaunchParams) => void) => void;
        };
        scene: Scene;
    }
}

const getURLArgs = () => {
    // extract settings from command line in non-prod builds only
    const config = {};

    const apply = (key: string, value: string) => {
        let obj: any = config;
        key.split('.').forEach((k, i, a) => {
            if (i === a.length - 1) {
                obj[k] = value;
            } else {
                if (!obj.hasOwnProperty(k)) {
                    obj[k] = {};
                }
                obj = obj[k];
            }
        });
    };

    const params = new URLSearchParams(window.location.search.slice(1));
    params.forEach((value: string, key: string) => {
        apply(key, value);
    });

    return config;
};

const main = async () => {
    // root events object
    const events = new Events();

    // url
    const url = new URL(window.location.href);

    // shared command queue for all async splat work (GPU readbacks + history mutations).
    // every consumer that needs ordering relative to other commands enqueues here.
    const commandQueue = new CommandQueue();

    // edit history (uses the shared queue internally)
    const editHistory = new EditHistory(events, commandQueue);

    // expose the queue as an event for any module that needs to serialise async work
    // alongside history mutations.
    events.function('queue', (fn: () => Promise<void> | void) => commandQueue.enqueue(fn));

    // init localization
    await i18n.init();

    // Configure WebP WASM for SOG format (used for both reading and writing)
    WebPCodec.wasmUrl = new URL('static/lib/webp/webp.wasm', document.baseURI).toString();

    // Run SOG writing inline rather than in worker threads. We don't ship
    // splat-transform's worker.mjs, so leaving the pool enabled makes it try to
    // spawn a worker that 404s; under SOG's parallel task load it then hangs
    // instead of falling back, producing an empty export.
    WorkerQueue.maxWorkers = 0;

    // register events that only need the events object (before UI is created)
    registerTimelineEvents(events);
    registerCameraPosesEvents(events);
    registerTrackManagerEvents(events);
    registerTransformHandlerEvents(events);
    registerPublishEvents(events);
    registerSaveRevisionEvents(events);
    registerIframeApi(events);

    // initialize shortcuts
    const shortcutManager = new ShortcutManager(events);
    events.function('shortcutManager', () => shortcutManager);

    // editor ui
    const editorUI = new EditorUI(events);

    // create the graphics device
    const graphicsDevice = await createGraphicsDevice(editorUI.canvas, {
        deviceTypes: ['webgl2'],
        antialias: false,
        depth: false,
        stencil: false,
        xrCompatible: false,
        powerPreference: 'high-performance'
    });

    const urlArgs = getURLArgs();

    const overrides = [
        urlArgs
    ];

    // resolve scene config
    const sceneConfig = getSceneConfig(overrides);

    // construct the manager
    const scene = new Scene(
        events,
        sceneConfig,
        editorUI.canvas,
        graphicsDevice,
        commandQueue
    );

    // colors
    const bgClr = new Color();
    const selectedClr = new Color();
    const unselectedClr = new Color();
    const lockedClr = new Color();

    const setClr = (target: Color, value: Color, event: string) => {
        if (!target.equals(value)) {
            target.copy(value);
            events.fire(event, target);
        }
    };

    const setBgClr = (clr: Color) => {
        setClr(bgClr, clr, 'bgClr');
    };
    const setSelectedClr = (clr: Color) => {
        setClr(selectedClr, clr, 'selectedClr');
    };
    const setUnselectedClr = (clr: Color) => {
        setClr(unselectedClr, clr, 'unselectedClr');
    };
    const setLockedClr = (clr: Color) => {
        setClr(lockedClr, clr, 'lockedClr');
    };

    events.on('setBgClr', (clr: Color) => {
        setBgClr(clr);
    });
    events.on('setSelectedClr', (clr: Color) => {
        setSelectedClr(clr);
    });
    events.on('setUnselectedClr', (clr: Color) => {
        setUnselectedClr(clr);
    });
    events.on('setLockedClr', (clr: Color) => {
        setLockedClr(clr);
    });

    events.function('bgClr', () => {
        return bgClr;
    });
    events.function('selectedClr', () => {
        return selectedClr;
    });
    events.function('unselectedClr', () => {
        return unselectedClr;
    });
    events.function('lockedClr', () => {
        return lockedClr;
    });

    events.on('bgClr', (clr: Color) => {
        const cnv = (v: number) => `${Math.max(0, Math.min(255, (v * 255))).toFixed(0)}`;
        document.body.style.backgroundColor = `rgba(${cnv(clr.r)},${cnv(clr.g)},${cnv(clr.b)},1)`;
    });
    events.on('selectedClr', (clr: Color) => {
        scene.forceRender = true;
    });
    events.on('unselectedClr', (clr: Color) => {
        scene.forceRender = true;
    });
    events.on('lockedClr', (clr: Color) => {
        scene.forceRender = true;
    });

    // initialize colors from application config
    const toColor = (value: { r: number, g: number, b: number, a: number }) => {
        return new Color(value.r, value.g, value.b, value.a);
    };
    setBgClr(toColor(sceneConfig.bgClr));
    setSelectedClr(toColor(sceneConfig.selectedClr));
    setUnselectedClr(toColor(sceneConfig.unselectedClr));
    setLockedClr(toColor(sceneConfig.lockedClr));

    // create the mask selection canvas
    const maskCanvas = document.createElement('canvas');
    const maskContext = maskCanvas.getContext('2d');
    maskCanvas.setAttribute('id', 'mask-canvas');
    maskContext.globalCompositeOperation = 'copy';

    const mask = {
        canvas: maskCanvas,
        context: maskContext
    };

    // tool manager
    const toolManager = new ToolManager(events);
    toolManager.register('rectSelection', new RectSelection(events, editorUI.toolsContainer.dom));
    toolManager.register('brushSelection', new BrushSelection(events, editorUI.toolsContainer.dom, mask));
    toolManager.register('floodSelection', new FloodSelection(events, editorUI.toolsContainer.dom, mask, editorUI.canvasContainer));
    toolManager.register('polygonSelection', new PolygonSelection(events, editorUI.toolsContainer.dom, mask));
    toolManager.register('lassoSelection', new LassoSelection(events, editorUI.toolsContainer.dom, mask));
    toolManager.register('sphereSelection', new SphereSelection(events, scene, editorUI.canvasContainer, editorUI.tooltips));
    toolManager.register('boxSelection', new BoxSelection(events, scene, editorUI.canvasContainer, editorUI.tooltips));
    toolManager.register('eyedropperSelection', new EyedropperSelection(events, editorUI.toolsContainer.dom, editorUI.canvasContainer));
    toolManager.register('move', new MoveTool(events, scene));
    toolManager.register('rotate', new RotateTool(events, scene));
    toolManager.register('scale', new ScaleTool(events, scene));
    toolManager.register('measure', new MeasureTool(events, scene, editorUI.canvasContainer));
    toolManager.register('orient', new OrientTool(events, scene, editorUI.toolsContainer.dom, editorUI.canvasContainer));

    const boundDimensionsOverlay = new BoundDimensionsOverlay(events, scene, editorUI.canvasContainer);

    editorUI.toolsContainer.dom.appendChild(maskCanvas);

    // show the active selection op (add/remove/intersect) at the cursor
    registerSelectCursor(events, editorUI.toolsContainer.dom);

    window.scene = scene;

    // register events that need scene or other dependencies
    registerEditorEvents(events, editHistory, scene);
    registerSelectionEvents(events, scene);
    registerSequenceEvents(events, scene);
    registerDocEvents(scene, events);
    registerRenderEvents(scene, events);
    initFileHandler(scene, events, editorUI.appContainer.dom);

    // apply stored user preferences and start capturing changes to them.
    // registered after the boot-time initialization events above so they are
    // never captured as user changes.
    registerPreferences(events, sceneConfig, urlArgs);

    // load async models
    scene.start();

    // handle load params
    const loadList = url.searchParams.getAll('load');
    const filenameList = url.searchParams.getAll('filename');
    for (const [i, value] of loadList.entries()) {
        // NB: URLSearchParams.getAll() already percent-decodes. Upstream ran a
        // second decodeURIComponent here, which corrupts PRESIGNED S3 URLs whose
        // query values contain %XX (e.g. Signature=...%3D -> ...=), producing a
        // broken signature (HTTP 400). Use the value as-is.
        const loadUrl = value;
        const filename = (i < filenameList.length && filenameList[i]) ||
            (loadUrl.split('?')[0].split('/').pop() ?? 'model.ply');

        // Scan-the-Wild: fetch the bytes and import as a File rather than passing
        // the URL through. A signed URL's query string otherwise becomes the
        // "filename" the loader inspects -> "Unsupported input file type";
        // importing a File lets type detection use the clean name. Splats are big
        // (~GB), so stream the download with a progress bar rather than a blank
        // canvas for a minute.
        events.fire('progressStart', `Downloading ${filename}...`);
        try {
            const response = await fetch(loadUrl);
            if (!response.ok || !response.body) {
                throw new Error(`failed to fetch ${filename} (${response.status})`);
            }
            const total = Number(response.headers.get('Content-Length')) || 0;
            const reader = response.body.getReader();
            const chunks: Uint8Array[] = [];
            let received = 0;
            for (;;) {
                const { done, value: chunk } = await reader.read();
                if (done) break;
                chunks.push(chunk);
                received += chunk.length;
                events.fire('progressUpdate', {
                    text: `Downloading ${filename}...`,
                    progress: total ? (100 * received) / total : 0
                });
            }
            const contents = new File(chunks as BlobPart[], filename);
            await events.invoke('import', [{ filename, contents }]);
        } finally {
            events.fire('progressEnd');
        }
    }

    // Scan-the-Wild: in "adjust-logo" mode the fragment carries the logo's
    // default placement — position the logo object (the last one loaded) there so
    // it opens where the pipeline normally puts it; the operator can then nudge it.
    const frag = new URLSearchParams(location.hash.replace(/^#/, ''));
    const logoOffset = frag.get('logo_offset');
    if (logoOffset && loadList.length >= 2) {
        const splats = events.invoke('scene.splats');
        const logo = splats?.[splats.length - 1];
        const subject = splats?.[splats.length - 2];
        if (logo && subject) {
            const [x, y, z] = logoOffset.split(',').map(Number);
            const s = Number(frag.get('logo_scale') ?? '1') || 1;
            // logo_offset is defined in the SUBJECT's own (file/PLY) frame — the
            // exact frame the worker's merge_logo bakes into. The editor displays
            // every splat through a 180° PLY→engine load flip, so placing the logo
            // at the raw offset would land it on the opposite side (X/Y negated).
            // Map the offset through the subject's world transform so the
            // separate-layer logo sits where the baked logo actually appears.
            const pos = new Vec3(x, y, z);
            subject.entity.getWorldTransform().transformPoint(pos, pos);

            // logo_rotation: Euler XYZ degrees in the same PLY frame, matching the
            // worker's merge_logo (composed Rz·Ry·Rx). Build it axis-by-axis so the
            // order is right regardless of PlayCanvas's Euler convention, then
            // compose with the subject's world rotation — the same PLY→engine flip
            // that positioning maps through. move() sets local rotation and would
            // otherwise drop the load flip, so we must fold it back in here.
            let rot: Quat | undefined;
            const logoRotation = frag.get('logo_rotation');
            if (logoRotation) {
                const [rx, ry, rz] = logoRotation.split(',').map(Number);
                if (rx || ry || rz) {
                    const qx = new Quat().setFromAxisAngle(new Vec3(1, 0, 0), rx);
                    const qy = new Quat().setFromAxisAngle(new Vec3(0, 1, 0), ry);
                    const qz = new Quat().setFromAxisAngle(new Vec3(0, 0, 1), rz);
                    const qLogo = new Quat().mul2(qz, qy);
                    qLogo.mul(qx);                    // qz·qy·qx  (= Rz·Ry·Rx)
                    rot = new Quat().mul2(subject.entity.getRotation(), qLogo);
                }
            }
            logo.move(pos, rot, new Vec3(s, s, s));
            // The logo is auto-selected on import and its rotation pivot pinned to
            // its position AT THAT MOMENT — the scene origin, before this move.
            // Moving a splat doesn't re-place the pivot (SuperSplat only re-places
            // it on a selection *change*), so rotate would otherwise swing around
            // the origin. Force a selection change to re-pin the pivot on the
            // logo's new spot: deselect (via null — the setter no-ops if the splat
            // is already selected) then reselect. Do it SYNCHRONOUSLY here: the
            // entity is already at its moved position, and a deferred version
            // (rAF/timeout) fires too late — animation frames are starved for
            // seconds while the ~GB splat is still uploading to the GPU.
            events.fire('selection', null);
            events.fire('selection', logo);
        }
    }

    // Frame the camera on ORIGIN (where the subject sits) rather than the scene
    // bound — a few floaters far from the subject otherwise blow the bound out and
    // the camera zooms miles away. camera.reset targets 0,0,0. (The ?load= path
    // doesn't auto-frame at all, which is why we do it here.)
    if (loadList.length > 0) {
        events.fire('camera.reset');
    }


    // handle OS-based file association in PWA mode
    if ('launchQueue' in window) {
        window.launchQueue.setConsumer(async (launchParams: LaunchParams) => {
            for (const file of launchParams.files) {
                await events.invoke('import', [{
                    filename: file.name,
                    contents: await file.getFile()
                }]);
            }
        });
    }
};

export { main };
